import { Box, chakra, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import {
  ANDROID_PLAY_STORE_URL,
  IOS_APP_STORE_URL,
  redirectToStoreIfMobile,
} from '@/utils/appStoreRedirect';

/**
 * Mobile landing page for /app/get. On load it sends iOS and Android visitors
 * straight to the matching store. When the platform can't be detected it shows
 * two store buttons and nothing else, so the visitor can pick manually.
 */
export default function AppGet(): React.JSX.Element | null {
  const [showButtons, setShowButtons] = React.useState(false);
  // Guard so the redirect fires once per mount even under StrictMode's
  // double-invoked effects (dev), avoiding a double navigation.
  const fired = React.useRef(false);

  React.useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!redirectToStoreIfMobile()) setShowButtons(true);
  }, []);

  // Render nothing while a store redirect is in flight.
  if (!showButtons) return null;

  return (
    <Flex minH="100vh" align="center" justify="center" px={6}>
      <VStack gap={4}>
        <StoreBadge
          href={IOS_APP_STORE_URL}
          icon={<FaApple />}
          topLine="Download on the"
          bottomLine="App Store"
        />
        <StoreBadge
          href={ANDROID_PLAY_STORE_URL}
          icon={<FaGooglePlay />}
          topLine="Get it on"
          bottomLine="Google Play"
        />
      </VStack>
    </Flex>
  );
}

interface StoreBadgeProps {
  href: string;
  icon: React.ReactNode;
  topLine: string;
  bottomLine: string;
}

const StoreBadge = ({ href, icon, topLine, bottomLine }: StoreBadgeProps): React.JSX.Element => (
  <chakra.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    _hover={{ opacity: 0.85 }}
  >
    <HStack
      bg="black"
      color="white"
      gap={3}
      px={5}
      py={3}
      minW="240px"
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.300"
    >
      <Box fontSize="3xl" flexShrink={0}>
        {icon}
      </Box>
      <Box textAlign="left" lineHeight="1.1" style={{ fontFamily: 'Lato' }}>
        <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wider">
          {topLine}
        </Text>
        <Text fontSize="xl" fontWeight="semibold">
          {bottomLine}
        </Text>
      </Box>
    </HStack>
  </chakra.a>
);
